import { Link } from "react-router-dom";
import { Star, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import AttorneyReviewNote from "@/components/AttorneyReviewNote";
import {
  breadcrumbSchema,
  faqPageSchema,
  legalServiceSchema,
  attorneySchema,
} from "@/lib/schemas";
import {
  TRUSTS_PILLAR,
  TRUST_SERVICES,
  TRUSTS_PHONE,
  TRUSTS_TOLL_FREE,
  findTrustService,
} from "@/lib/trusts";

/**
 * TrustsPillar — /trusts
 *
 * The authority hub. Six modules:
 *   1. Hero + lede
 *   2. "Which trust is right for me?"
 *   3. Trust planning services (8 tiles)
 *   4. Process (5 steps)
 *   5. FAQs
 *   6. Closing CTA + Attorney Advertising note
 */
export default function TrustsPillar() {
  const spotlight = findTrustService("business-owner-trust-planning");
  const spotlightPreview = (spotlight?.priorityFaqs || []).slice(0, 3);
  const schemas = [
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Trusts", path: "/trusts" },
    ]),
    legalServiceSchema({
      name: "Texas Trust Planning",
      description: TRUSTS_PILLAR.description,
      url: "https://lonestarcounsel.com/trusts",
    }),
    attorneySchema(),
    faqPageSchema(TRUSTS_PILLAR.faqs),
  ];

  return (
    <div className="bg-ivory" data-testid="trusts-pillar">
      <SEO
        title={TRUSTS_PILLAR.title}
        description={TRUSTS_PILLAR.description}
        path="/trusts"
        schema={schemas}
      />

      {/* HERO */}
      <section className="relative panel-navy grain grain-dark py-24 md:py-36" data-testid="trusts-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="text-[10px] uppercase tracking-[0.42em] text-brass font-medium">
            Trusts & Estate Planning · Texas
          </div>
          <h1 className="mt-8 font-display text-ivory text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-4xl" data-testid="trusts-h1">
            {TRUSTS_PILLAR.h1}.
          </h1>
          <p className="mt-8 font-cormorant italic text-2xl md:text-3xl text-brass leading-snug max-w-3xl">
            {TRUSTS_PILLAR.tagline}
          </p>
          <div className="mt-10 max-w-3xl space-y-5 text-ivory/85 font-light leading-relaxed">
            {TRUSTS_PILLAR.directAnswer && (
              <p className="text-lg md:text-xl text-ivory/90 leading-relaxed" data-testid="trusts-direct-answer">
                {TRUSTS_PILLAR.directAnswer}
              </p>
            )}
            {TRUSTS_PILLAR.lede.map((p, i) => (
              <p key={i} className={i === 0 ? "text-lg md:text-xl" : "text-base md:text-lg"}>
                {p}
              </p>
            ))}
          </div>
          {TRUSTS_PILLAR.attorneyReviewed && (
            <div className="mt-8 max-w-3xl">
              <AttorneyReviewNote reviewed={TRUSTS_PILLAR.attorneyReviewed} variant="dark" compact />
            </div>
          )}
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link to="/intake" className="btn-brass inline-flex items-center gap-2" data-testid="trusts-hero-cta">
              <Star size={11} className="fill-current" />
              Request a Confidential Trust Planning Conference
            </Link>
            <a
              href={`tel:${TRUSTS_PHONE.replace(/\D/g, "")}`}
              className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/50 hover:border-brass pb-1"
              data-testid="trusts-hero-phone"
            >
              <Phone size={11} className="inline mr-2" />
              Call {TRUSTS_PHONE} · Toll-Free {TRUSTS_TOLL_FREE}
            </a>
          </div>
        </div>
      </section>

      {/* WHICH TRUST IS RIGHT */}
      <section className="bg-ivory py-24 md:py-32" data-testid="trusts-which">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">Framing the question</div>
          <h2 className="mt-6 font-display text-navy text-4xl md:text-5xl leading-[1.05]" data-testid="trusts-which-h2">
            {TRUSTS_PILLAR.whichHeadline}
          </h2>
          <p className="mt-8 text-lg md:text-xl text-navy/80 leading-relaxed">
            {TRUSTS_PILLAR.whichBody}
          </p>
          <p className="mt-8 font-cormorant italic text-2xl md:text-3xl text-oxblood leading-snug">
            {TRUSTS_PILLAR.whichAccent}
          </p>
        </div>
      </section>

      {/* AUTHORITY SPOTLIGHT — Business-Owner Trust Planning */}
      {spotlight && (
        <section className="bg-ivory hairline-top py-20 md:py-28" data-testid="trusts-spotlight">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-5">
                <div className="text-[10px] uppercase tracking-[0.36em] text-brass font-medium" data-testid="trusts-spotlight-kicker">
                  <Star size={9} className="inline mr-2 fill-brass text-brass" aria-hidden />
                  Editor's Pick
                </div>
                <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]" data-testid="trusts-spotlight-h2">
                  Coordinated planning for{" "}
                  <span className="italic font-cormorant text-oxblood">Texas business owners.</span>
                </h2>
                <p className="mt-6 text-navy/85 font-serif leading-relaxed">
                  A business owner's estate plan is not one document. It is the coordinated set — revocable trust, company agreement, buy-sell, powers of attorney, and beneficiary designations — that describes the same event. Our most-read authority page covers what to draft, what to review, and what to update.
                </p>
                <Link
                  to={`/trusts/${spotlight.slug}`}
                  className="mt-8 inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-oxblood hover:text-navy transition-colors font-medium border-b border-oxblood/40 hover:border-navy pb-1"
                  data-testid="trusts-spotlight-cta"
                >
                  Read the full guide &rarr;
                </Link>
              </div>
              <div className="lg:col-span-7">
                <div className="bg-linen p-8 md:p-10 border-t-2 border-brass" data-testid="trusts-spotlight-preview">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-navy/60 font-medium">
                    Preview &mdash; three of eight priority questions
                  </div>
                  <ul className="mt-6 space-y-6">
                    {spotlightPreview.map(({ q, a }, i) => (
                      <li key={i} className="hairline-top pt-5" data-testid={`trusts-spotlight-preview-${i}`}>
                        <h3 className="font-display text-lg md:text-xl text-navy leading-snug">
                          {q}
                        </h3>
                        <p className="mt-2 text-sm text-navy/75 leading-relaxed font-serif line-clamp-3">
                          {a}
                        </p>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/trusts/${spotlight.slug}#ts-priority-faqs`}
                    className="mt-8 inline-block text-[10px] uppercase tracking-[0.32em] text-brass font-medium border-b border-brass/50 hover:border-brass pb-0.5"
                    data-testid="trusts-spotlight-preview-cta"
                  >
                    See all eight priority questions &rarr;
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* SERVICES INDEX */}
      <section className="bg-linen hairline-top py-24 md:py-32" data-testid="trusts-services-index">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="kicker">Trust Planning Services</div>
          <h2 className="mt-6 font-display text-navy text-4xl md:text-5xl leading-[1.05]">
            Eight disciplines.{" "}
            <span className="italic font-cormorant text-oxblood">One coordinated plan.</span>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
            {TRUST_SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/trusts/${s.slug}`}
                className="group hairline-top pt-6 pb-1 block hover:bg-ivory/50 transition-colors -mx-4 px-4"
                data-testid={`trusts-service-tile-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-display text-2xl md:text-3xl text-navy leading-[1.1] group-hover:text-oxblood transition-colors">
                    {s.menu}
                  </h3>
                  <Star size={11} className="text-brass fill-brass mt-2 shrink-0" />
                </div>
                <p className="mt-3 text-navy/75 leading-relaxed">{s.menuDesc}</p>
                <span className="mt-4 inline-block text-[10px] uppercase tracking-[0.32em] text-brass font-medium border-b border-brass/40 pb-0.5">
                  Read more &rarr;
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-ivory py-24 md:py-32" data-testid="trusts-process">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="kicker">The Lone Star Counsel Process</div>
          <h2 className="mt-6 font-display text-navy text-4xl md:text-5xl leading-[1.05]">
            How the plan is built.
          </h2>
          <ol className="mt-12 space-y-8">
            {TRUSTS_PILLAR.process.map(([h, b], i) => (
              <li key={i} className="grid grid-cols-12 gap-6 hairline-top pt-6" data-testid={`trusts-process-${i}`}>
                <div className="col-span-12 md:col-span-2 font-display text-3xl text-brass">
                  0{i + 1}
                </div>
                <div className="col-span-12 md:col-span-10">
                  <h3 className="font-display text-xl md:text-2xl text-navy leading-snug">{h}</h3>
                  <p className="mt-3 text-navy/75 leading-relaxed">{b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-linen hairline-top py-24 md:py-32" data-testid="trusts-faqs">
        <div className="max-w-[1000px] mx-auto px-6 md:px-12">
          <div className="kicker">Frequently asked</div>
          <h2 className="mt-6 font-display text-navy text-4xl md:text-5xl leading-[1.05]">
            Trust planning, without the mystery.
          </h2>
          <dl className="mt-12 space-y-8">
            {TRUSTS_PILLAR.faqs.map(({ q, a }, i) => (
              <div key={i} className="hairline-top pt-6" data-testid={`trusts-faq-${i}`}>
                <dt className="font-display text-xl md:text-2xl text-navy leading-snug">{q}</dt>
                <dd className="mt-3 text-navy/80 leading-relaxed">{a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CONTEXTUAL AUTHORITY LINKS (optional) */}
      {TRUSTS_PILLAR.contextualLinks && (
        <section className="bg-ivory hairline-top py-20 md:py-28" data-testid="trusts-contextual-links">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="kicker">Related counsel</div>
            <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
              Coordinate trust planning with{" "}
              <span className="italic font-cormorant text-oxblood">the surrounding disciplines.</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="trusts-contextual-list">
              {TRUSTS_PILLAR.contextualLinks.map((c, i) => (
                <Link
                  key={i}
                  to={c.href}
                  className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3"
                  data-testid={`trusts-contextual-${i}`}
                >
                  <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                    {c.label}
                  </div>
                  <h3 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                    {c.desc}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CLOSING CTA */}
      <section className="panel-navy grain grain-dark py-24 md:py-32 relative overflow-hidden" data-testid="trusts-closing-cta">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12 text-center">
          <Star size={11} className="text-brass fill-brass mx-auto" />
          <h2 className="mt-8 font-display text-ivory text-3xl md:text-5xl leading-[1.05] max-w-3xl mx-auto">
            {TRUSTS_PILLAR.closing}
          </h2>
          <p className="mt-8 max-w-2xl mx-auto font-cormorant italic text-lg md:text-xl text-ivory/80 leading-snug">
            {TRUSTS_PILLAR.closingSub}
          </p>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <Link to="/intake" className="btn-brass" data-testid="trusts-closing-intake">
              Request a Confidential Consultation
            </Link>
            <Link to="/contact" className="text-[11px] uppercase tracking-[0.32em] text-ivory/85 hover:text-brass transition-colors font-medium border-b border-brass/40 hover:border-brass pb-1" data-testid="trusts-closing-contact">
              or contact our office
            </Link>
          </div>
          <div className="mt-14 max-w-3xl mx-auto text-[10px] uppercase tracking-[0.28em] text-ivory/50 leading-relaxed">
            Attorney Advertising. Pratt Law Group, PLLC d/b/a LONE STAR ★ COUNSEL. The information on this page is provided for general educational purposes and does not constitute legal or tax advice. No attorney-client relationship is formed by using this site or contacting the firm. Selection and implementation of a trust depend on the client's specific family, assets, tax, fiduciary, and succession circumstances. Prior results do not guarantee a similar outcome.
          </div>
          <div className="max-w-3xl mx-auto">
            <AttorneyReviewNote reviewed="February 2026" variant="dark" compact />
          </div>
        </div>
      </section>
    </div>
  );
}
